import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Megaphone, Pin, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdCard({ ad, onCopy, onAdClick, copied, variant = "marquee", ariaHidden = false }) {
    const hasLink = !!ad.link?.trim();
    const openLink = () => {
        if (!hasLink) return;
        if (onAdClick && !ariaHidden) onAdClick(ad);
        window.open(ad.link, "_blank", "noopener,noreferrer");
    };

    const stopCopy = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onCopy(ad);
    };

    const isPinnedBanner = variant === "pinned";

    return (
        <div
            data-testid={ariaHidden ? undefined : `strip-item-${ad.id}`}
            aria-hidden={ariaHidden || undefined}
            onClick={hasLink ? openLink : undefined}
            className={`shrink-0 inline-flex items-center gap-2 md:gap-3 rounded-lg border px-3 md:px-4 py-2 md:py-2.5 transition-all min-w-[180px] md:min-w-[220px] ${
                isPinnedBanner
                    ? "w-full bg-gradient-to-l from-[#FFF9E5] via-white to-[#FFF9E5] border-[#D4AF37] shadow-md min-w-0"
                    : "bg-white border-[#4FB8B0]/30 hover:border-[#4FB8B0]"
            } ${hasLink ? "cursor-pointer hover:shadow-md" : "cursor-default"}`}
        >
            {isPinnedBanner && (
                <span className="shrink-0 bg-[#D4AF37] text-[#0F2A28] rounded-full p-1.5" aria-label="pinned">
                    <Pin size={14} />
                </span>
            )}
            {ad.image_url && (
                <img
                    src={ad.image_url}
                    alt=""
                    aria-hidden="true"
                    width={isPinnedBanner ? 48 : 40}
                    height={isPinnedBanner ? 48 : 40}
                    className={`shrink-0 rounded object-cover border border-[#4FB8B0]/30 ${
                        isPinnedBanner ? "w-12 h-12" : "w-10 h-10"
                    }`}
                />
            )}
            <div className="flex-1 min-w-0 flex items-center gap-2 md:gap-3 flex-nowrap">
                <div className="flex-1 min-w-0 flex flex-col leading-tight">
                    <span className="font-bold text-[#0F2A28] text-sm md:text-base whitespace-nowrap overflow-hidden text-ellipsis">
                        {ad.title}
                    </span>
                    {ad.description && (
                        <span className="text-xs md:text-sm text-zinc-600 whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                            {ad.description}
                        </span>
                    )}
                </div>
                {ad.code && (
                    <button
                        onClick={stopCopy}
                        data-testid={ariaHidden ? undefined : `strip-copy-${ad.id}`}
                        className="shrink-0 inline-flex items-center gap-1 md:gap-1.5 bg-[#F5FBFA] border border-dashed border-[#4FB8B0] rounded px-2 md:px-2.5 py-0.5 md:py-1 text-[11px] md:text-xs font-numeric font-bold text-[#1E5E5A] hover:bg-[#4FB8B0]/20 transition-colors"
                        title="نسخ الكود"
                    >
                        {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                        <span>{ad.code}</span>
                    </button>
                )}
                {hasLink && (
                    <span className="shrink-0 text-[#4FB8B0] inline-flex items-center gap-1 text-xs font-bold">
                        <ExternalLink size={12} />
                        <span className="hidden md:inline">اضغط للانتقال</span>
                    </span>
                )}
            </div>
        </div>
    );
}

export default function AdMarquee({ endpoint = "/strip", title = "إعلانات", data_testid_prefix = "strip" } = {}) {
    const [pinned, setPinned] = useState(null);
    const [marquee, setMarquee] = useState([]);
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const fingerprint = (arr) =>
            arr.map((a) => `${a.id}:${a.title}:${a.code}:${a.link}:${a.image_url ? 1 : 0}`).join("|");
        let lastPinnedFp = "";
        let lastMarqueeFp = "";
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}${endpoint}`);
                if (cancelled) return;
                const newPinnedFp = data.pinned ? fingerprint([data.pinned]) : "";
                const newMarqueeFp = fingerprint(data.marquee || []);
                if (newPinnedFp !== lastPinnedFp) {
                    setPinned(data.pinned || null);
                    lastPinnedFp = newPinnedFp;
                }
                if (newMarqueeFp !== lastMarqueeFp) {
                    setMarquee(data.marquee || []);
                    lastMarqueeFp = newMarqueeFp;
                }
            } catch {
                // silent
            }
        };
        load();
        const t = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [endpoint]);

    const copyCode = async (ad) => {
        try {
            await navigator.clipboard.writeText(ad.code);
            setCopiedId(ad.id);
            // Count code copy as a click on this strip ad.
            axios.post(`${API}/strip/${ad.id}/click`).catch(() => {});
            toast.success(`تم نسخ الكود: ${ad.code}`);
            setTimeout(() => setCopiedId((c) => (c === ad.id ? null : c)), 2000);
        } catch {
            toast.error("تعذّر النسخ");
        }
    };

    const trackClick = (ad) => {
        // Fire-and-forget; failure shouldn't block the redirect.
        axios.post(`${API}/strip/${ad.id}/click`).catch(() => {});
    };

    // JavaScript-driven scroll — measures the actual group1 width at every
    // frame and wraps at exactly that value. This avoids CSS's stale width
    // problem where `translate(-50%)` uses a cached width.
    const trackRef = useRef(null);
    const wrapRef = useRef(null);
    const xRef = useRef(0);
    const pausedUntilRef = useRef(0);
    const dragRef = useRef({ dragging: false, startX: 0, startXRef: 0, moved: 0 });
    const [repeats, setRepeats] = useState(1);

    // Merge pinned (if any) into the visible marquee so it scrolls with the rest.
    // Inside the strip it renders as a regular ad — the "pinned" styling is
    // reserved for the dedicated banner section above.
    const combined = pinned ? [pinned, ...marquee] : marquee;

    // Grow `repeats` until one group exceeds 1.5× the viewport so screen is
    // always filled with content — no matter where the animation is.
    useEffect(() => {
        if (combined.length === 0) return;
        const doMeasure = () => {
            const track = trackRef.current;
            const wrap = wrapRef.current;
            if (!track || !wrap) return;
            const g1 = track.children[0];
            if (!g1) return;
            const groupWidth = g1.getBoundingClientRect().width;
            const wrapWidth = wrap.getBoundingClientRect().width;
            if (groupWidth < wrapWidth * 1.5 && repeats < 30) {
                setRepeats((r) => Math.min(r + 1, 30));
            }
        };
        doMeasure();
        const t = setTimeout(doMeasure, 300);
        window.addEventListener("resize", doMeasure);
        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", doMeasure);
        };
    }, [combined, repeats]);

    // Reset repeats + position when the ad list itself changes.
    useEffect(() => {
        setRepeats(1);
        xRef.current = 0;
    }, [combined.length]);

    // rAF loop — mounts ONCE and runs forever. Reads the current group1 width
    // every frame from the DOM. Respects `pausedUntilRef` (drag → 3s cooldown).
    useEffect(() => {
        let raf;
        let lastTime = performance.now();
        const SPEED = 50; // px/s
        const tick = (now) => {
            const dt = Math.min(0.1, (now - lastTime) / 1000);
            lastTime = now;
            const track = trackRef.current;
            if (track && track.children[0]) {
                const half = track.children[0].getBoundingClientRect().width;
                if (half > 0) {
                    const paused = dragRef.current.dragging || now < pausedUntilRef.current;
                    if (!paused) {
                        xRef.current -= SPEED * dt;
                    }
                    // Keep x in a valid range regardless (also useful during drag)
                    while (xRef.current <= -half) xRef.current += half;
                    while (xRef.current > 0) xRef.current -= half;
                    track.style.transform = `translate3d(${xRef.current}px, 0, 0)`;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    // Drag handlers — user can push the strip left/right; after release the
    // animation pauses for 3 seconds so the user can read what they landed on.
    // NOTE: we do NOT setPointerCapture on down — browsers retarget subsequent
    // click events to the capture element, which breaks click-to-open. We only
    // capture the pointer AFTER movement passes a threshold (real drag intent).
    const onPointerDown = (e) => {
        const target = e.target;
        if (target.closest("button") || target.closest("a")) return;
        dragRef.current.dragging = true;
        dragRef.current.startX = e.clientX;
        dragRef.current.startXRef = xRef.current;
        dragRef.current.moved = 0;
        dragRef.current.captured = false;
        dragRef.current.pointerId = e.pointerId;
    };
    const onPointerMove = (e) => {
        if (!dragRef.current.dragging) return;
        const dx = e.clientX - dragRef.current.startX;
        dragRef.current.moved = Math.max(dragRef.current.moved, Math.abs(dx));
        if (!dragRef.current.captured && dragRef.current.moved > 8) {
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
                dragRef.current.captured = true;
            } catch {}
        }
        if (dragRef.current.captured) {
            xRef.current = dragRef.current.startXRef + dx;
        }
    };
    const endDrag = (e) => {
        if (!dragRef.current.dragging) return;
        const wasDragging = dragRef.current.captured;
        dragRef.current.dragging = false;
        if (wasDragging) {
            pausedUntilRef.current = performance.now() + 3000; // 3s cooldown
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {}
            dragRef.current.captured = false;
        }
    };
    // Prevent accidental link-open right after a real drag.
    const onClickCapture = (e) => {
        if (dragRef.current.moved > 20) {
            e.stopPropagation();
            e.preventDefault();
            dragRef.current.moved = 0;
        }
    };

    // Build one group by repeating the combined ads N times so it packs the viewport fully.
    const groupItems = [];
    if (combined.length > 0) {
        for (let i = 0; i < repeats; i++) groupItems.push(...combined);
    }

    return (
        <div
            data-testid={`${data_testid_prefix}-root`}
            className="bg-[#F5FBFA] border-b border-[#4FB8B0]/30"
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
                {/* Label — centered, spans full width with dashed rules on both sides */}
                <div className="flex items-center gap-3 py-2">
                    <span className="flex-1 border-t border-dashed border-[#4FB8B0]/40" />
                    <span
                        data-testid={`${data_testid_prefix}-title`}
                        className="inline-flex items-center gap-2 font-bold text-[#0F2A28] text-sm tracking-widest"
                    >
                        <Megaphone size={16} className="text-[#1E5E5A]" />
                        {title}
                    </span>
                    <span className="flex-1 border-t border-dashed border-[#4FB8B0]/40" />
                </div>

                {/* Pinned banner — shown above the strip with the distinct
                    gold styling. The same ad also appears inside the strip
                    below (but rendered as a normal card to blend in). */}
                {pinned && (
                    <div data-testid="strip-pinned" className="py-2">
                        <AdCard ad={pinned} onCopy={copyCode} onAdClick={trackClick} copied={copiedId === pinned.id} variant="pinned" />
                    </div>
                )}

                {/* Marquee strip — the pinned ad (if any) is also included here
                    but styled like the rest so it doesn't stand out inside the loop.
                    dir="ltr" on the wrap is CRITICAL: without it the RTL page
                    context anchors the track to the right edge, creating empty
                    space on the right when translating left. */}
                {combined.length > 0 ? (
                    <div
                        ref={wrapRef}
                        className="overflow-hidden py-2 touch-pan-y select-none cursor-grab active:cursor-grabbing"
                        data-testid="strip-scroll"
                        dir="ltr"
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        onClickCapture={onClickCapture}
                    >
                        <div
                            ref={trackRef}
                            className="flex flex-nowrap"
                            style={{ willChange: "transform", width: "max-content" }}
                            dir="ltr"
                        >
                            {/* First group */}
                            <div className="flex flex-nowrap gap-3 pe-3">
                                {groupItems.map((ad, i) => (
                                    <AdCard
                                        key={`a-${ad.id}-${i}`}
                                        ad={ad}
                                        onCopy={copyCode}
                                        onAdClick={trackClick}
                                        copied={copiedId === ad.id}
                                    />
                                ))}
                            </div>
                            {/* Identical second group — makes the loop truly seamless */}
                            <div className="flex flex-nowrap gap-3 pe-3" aria-hidden="true">
                                {groupItems.map((ad, i) => (
                                    <AdCard
                                        key={`b-${ad.id}-${i}`}
                                        ad={ad}
                                        onCopy={copyCode}
                                        copied={copiedId === ad.id}
                                        ariaHidden
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    !pinned && (
                        <div
                            data-testid={`${data_testid_prefix}-empty`}
                            className="py-2 text-xs text-zinc-500 italic"
                        >
                            لا توجد {title} حالياً.
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
